/* ===================== Earnings (Astrologer) =====================
   Per-booking earnings breakdown — Booking ID, Date, Customer, Type,
   Rate, GST, Commission and Net Earning — with a client-side CSV
   export. Mirrors the reference "Earnings Breakdown" screen.

   Data model: booking rates are gross and GST-INCLUSIVE; the platform
   keeps its commission on the GST-exclusive (taxable) base:
     gst        = rate × gstPercent / (100 + gstPercent)
     taxable    = rate − gst
     commission = taxable × commissionPercent
     net        = taxable − commission
   (Same model as AstrologerOverviewTab's stat cards.)

   Live data: GET /astrologer/earnings (api.astrologers.earnings), which
   returns the rows already costed, the totals, and the rates they were
   costed at. BOTH percentages are the admin's (default 30% commission on an
   18%-GST-inclusive rate), so this screen prints what the API reports
   instead of assuming them — and a completed consultation keeps the figures
   it was settled at even after the admin changes the rate. The constants
   below are only a fallback for a payload that predates the rates block. */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { appUrl } from '@astro/config'

const FALLBACK_RATES = { percent: 30, gst_percent: 18, is_active: true, overridden: false, house_exempt: false }

/* ── Response normalisation — the upstream shape is loosely documented, so
      dig the rows out of whatever envelope/paginator wraps them and map the
      field-name variations onto the keys the table renders. ── */

const ROW_KEYS = ['rows', 'earnings', 'data', 'list', 'items', 'bookings']
function rowsFrom(res, depth = 0) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object' || depth > 3) return []
  for (const key of ROW_KEYS) {
    if (key in res) {
      const found = rowsFrom(res[key], depth + 1)
      if (found.length) return found
    }
  }
  return []
}

function normalizeRow(r) {
  const id = r.booking_id ?? r.id ?? null
  return {
    booking_id: id,
    booking_code: r.booking_code ?? r.booking_number ?? (id != null ? `#${id}` : '—'),
    date: r.date ?? r.scheduled_at ?? r.created_at ?? null,
    customer: { name: r.customer?.name ?? r.user?.name ?? r.customer_name ?? r.user_name ?? null },
    consultation_type: r.consultation_type ?? r.type ?? null,
    rate: r.rate ?? r.amount ?? r.total_amount ?? 0,
    // Server-computed money fields, when present, win in breakdown().
    gst: r.gst ?? r.gst_amount ?? null,
    taxable_value: r.taxable_value ?? r.taxable ?? null,
    commission: r.commission ?? r.commission_amount ?? null,
    net_earning: r.net_earning ?? r.net_amount ?? r.net ?? null,
  }
}

/** The rates the API costed these rows at, or the historical defaults. */
function ratesFrom(res) {
  const rates = res?.data?.rates ?? res?.rates
  return rates && rates.percent != null ? { ...FALLBACK_RATES, ...rates } : FALLBACK_RATES
}

/** One row's money figures — server-provided fields win, else derive from the
 *  rate using whatever percentages the API reported. */
function breakdown(row, rates = FALLBACK_RATES) {
  const gstRate = Number(rates.gst_percent) / 100
  const commissionRate = Number(rates.percent) / 100
  const rate = Number(row.rate) || 0
  const gst = row.gst != null ? Number(row.gst) : rate * (gstRate / (1 + gstRate))
  const taxable = row.taxable_value != null ? Number(row.taxable_value) : rate - gst
  const commission = row.commission != null ? Number(row.commission) : taxable * commissionRate
  const net = row.net_earning != null ? Number(row.net_earning) : taxable - commission
  return { rate, gst, taxable, commission, net }
}

/** A percentage for a column header: "18" / "17.5", never "17.500000". */
function pct(value) {
  const n = Number(value) || 0
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)))
}

/** Indian-grouped amount with paise, e.g. 5932.203 → "5,932.20". */
function n2(v) {
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function dateLabel(iso) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

function typeLabel(t) {
  const s = String(t || 'video')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/* ── CSV export — same figures as the table so the file always matches
      the screen (the reference app computed them differently in its two
      code paths; we deliberately share one breakdown()). ── */
function csvCell(v) {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function exportCsv(rows, rates) {
  const header = ['Booking ID', 'Date', 'Customer', 'Type', 'Rate (INR)', `GST ${pct(rates.gst_percent)}% (INR)`, `Commission ${pct(rates.percent)}% (INR)`, 'Net Earning (INR)']
  const lines = rows.map((r) => {
    const m = breakdown(r, rates)
    return [r.booking_code, dateLabel(r.date), r.customer?.name ?? '', typeLabel(r.consultation_type),
    m.rate.toFixed(2), m.gst.toFixed(2), m.commission.toFixed(2), m.net.toFixed(2)].map(csvCell).join(',')
  })
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `earnings-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/* ── Skeleton shown while the earnings read loads — mirrors the page:
      header card, then the table card with pulsing rows. ── */
function EarningsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading your earnings">
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl flex items-center justify-between gap-4">
        <div className="space-y-2">
          <div className="h-4 w-44 bg-stone-100 rounded-full" />
          <div className="h-2.5 w-64 bg-stone-100 rounded-full" />
        </div>
        <div className="h-9 w-32 bg-stone-100 rounded-xl shrink-0" />
      </div>
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
        <div className="h-3 w-28 bg-stone-100 rounded-full mb-5" />
        <div className="space-y-2">
          <div className="h-10 bg-stone-100 rounded-lg" />
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-9 bg-stone-50 rounded-lg" />
          ))}
          <div className="h-10 bg-stone-100 rounded-lg" />
        </div>
      </div>
    </div>
  )
}

export default function EarningsTab({ onSwitch }) {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['dashboard', 'astrologer-earnings'],
    queryFn: () => api.astrologers.earnings(),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
    select: (res) => ({
      rows: rowsFrom(res).map(normalizeRow),
      rates: ratesFrom(res),
      // The API's own totals, when it sends them — the screen then agrees with
      // the admin's commission report to the paisa.
      summary: res?.data?.summary ?? null,
    }),
  })
  const rows = query.data?.rows ?? []
  const rates = query.data?.rates ?? FALLBACK_RATES
  const summary = query.data?.summary ?? null
  const totals = useMemo(() => {
    if (summary) {
      return {
        rate: Number(summary.gross) || 0,
        gst: Number(summary.gst) || 0,
        commission: Number(summary.commission) || 0,
        net: Number(summary.net) || 0,
      }
    }
    return rows.reduce(
      (t, r) => {
        const m = breakdown(r, rates)
        return { rate: t.rate + m.rate, gst: t.gst + m.gst, commission: t.commission + m.commission, net: t.net + m.net }
      },
      { rate: 0, gst: 0, commission: 0, net: 0 },
    )
  }, [rows, rates, summary])

  if (query.isPending) return <EarningsSkeleton />

  return (
    <div className="space-y-6">

      {/* ── Header card — title + actions, mirrors the reference banner ── */}
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-stone-900 font-royal">Earnings Breakdown</h2>
          <p className="text-xs text-stone-500 mt-1">Detailed per-booking earnings, GST, and commission.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => exportCsv(rows, rates)}
            disabled={!rows.length}
            className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 rounded-xl shadow-sm hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm"
          >
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* ── Empty / error state — no demo rows: what the API returns is what shows ── */}
      {!rows.length ? (
        <div className="bg-white p-6 sm:p-10 border border-stone-100 shadow-sm rounded-2xl text-center">
          <div className="text-4xl mb-3 opacity-50">💰</div>
          <h3 className="text-sm font-bold text-stone-800">
            {query.isError ? 'Couldn’t load your earnings' : 'No earnings yet'}
          </h3>
          <p className="text-xs text-stone-500 mt-1 max-w-sm mx-auto">
            {query.isError
              ? 'Something went wrong while fetching your earnings. Please try again.'
              : 'Completed consultations will appear here with their GST, commission and net earning breakdown.'}
          </p>
          {query.isError ? (
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-4 border border-stone-200 px-5 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              Retry
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onSwitch?.('appoinments')}
              className="mt-4 border border-stone-200 px-5 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              View Appointments
            </button>
          )}
        </div>
      ) : (
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
        <div className="border-b border-stone-100 mb-4">
          <span className="inline-block pb-2 text-sm font-bold text-stone-900 border-b-2 border-amber-600">Earnings Table</span>
        </div>

        {/* ── Below md: one card per booking ──
            Eight financial columns cannot honestly fit a phone; the table's
            min-w-[720px] just pushed them into a sideways scroll where the Net
            Earning — the only figure most astrologers open this tab for — sat
            off-screen. The card leads with that figure instead. */}
        <div className="md:hidden space-y-3">
          {rows.map((r, i) => {
            const m = breakdown(r, rates)
            return (
              <div key={r.booking_id ?? i} className="border border-stone-200 rounded-xl p-3.5 bg-stone-50/60">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a
                      href={appUrl('jyotish', `/astrologer/appointments/${r.booking_id}`)}
                      className="text-[#A67C00] text-sm font-bold underline underline-offset-2 hover:text-amber-700 wrap-break-word"
                      title="View booking details"
                    >
                      {r.booking_code}
                    </a>
                    <p className="text-xs text-stone-600 mt-0.5 truncate">{r.customer?.name ?? '—'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-stone-400">Net</p>
                    <p className="text-base font-bold text-stone-900 tabular-nums leading-tight">₹{n2(m.net)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 pt-3 border-t border-stone-200">
                  <EarningFact label="Date" value={dateLabel(r.date)} />
                  <EarningFact label="Type" value={typeLabel(r.consultation_type)} />
                  <EarningFact label="Rate (₹)" value={n2(m.rate)} />
                  <EarningFact label={`GST (${pct(rates.gst_percent)}%)`} value={n2(m.gst)} />
                  <EarningFact label={`Commission (${pct(rates.percent)}%)`} value={n2(m.commission)} />
                </div>
              </div>
            )
          })}
          {/* The tfoot's totals row, as the list's closing card. */}
          <div className="rounded-xl bg-[#3D0A0A] text-white p-3.5">
            <p className="text-[10px] font-bold uppercase tracking-wider">
              Total · {rows.length} booking{rows.length === 1 ? '' : 's'}
            </p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 pt-3 border-t border-white/15">
              <EarningFact label="Rate (₹)" value={n2(totals.rate)} dark />
              <EarningFact label="GST" value={n2(totals.gst)} dark />
              <EarningFact label="Commission" value={n2(totals.commission)} dark />
              <div>
                <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-white/50">Net Earning</p>
                <p className="text-sm font-bold text-[#D4A017] tabular-nums mt-1">{n2(totals.net)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[720px]">
            <thead>
              <tr className="border border-stone-200 bg-stone-50">
                {['Booking ID', 'Date', 'Customer', 'Type', 'Rate (₹)', `GST (${pct(rates.gst_percent)}%)`, `Commission (${pct(rates.percent)}%)`, 'Net Earning (₹)'].map((h) => (
                  <th key={h} className="border border-stone-200 px-4 py-3 text-xs font-bold text-stone-800">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const m = breakdown(r, rates)
                return (
                  <tr key={r.booking_id ?? i} className={i % 2 === 0 ? 'bg-stone-50/60' : 'bg-white'}>
                    <td className="border border-stone-200 px-4 py-3 text-sm">
                      <a
                        href={appUrl('jyotish', `/astrologer/appointments/${r.booking_id}`)}
                        className="text-[#A67C00] font-semibold underline underline-offset-2 hover:text-amber-700"
                        title="View booking details"
                      >
                        {r.booking_code}
                      </a>
                    </td>
                    <td className="border border-stone-200 px-4 py-3 text-sm text-stone-700">{dateLabel(r.date)}</td>
                    <td className="border border-stone-200 px-4 py-3 text-sm text-stone-700">{r.customer?.name ?? '—'}</td>
                    <td className="border border-stone-200 px-4 py-3 text-sm text-stone-700">{typeLabel(r.consultation_type)}</td>
                    <td className="border border-stone-200 px-4 py-3 text-sm text-stone-700 tabular-nums">{n2(m.rate)}</td>
                    <td className="border border-stone-200 px-4 py-3 text-sm text-stone-700 tabular-nums">{n2(m.gst)}</td>
                    <td className="border border-stone-200 px-4 py-3 text-sm text-stone-700 tabular-nums">{n2(m.commission)}</td>
                    <td className="border border-stone-200 px-4 py-3 text-sm font-bold text-stone-900 tabular-nums">{n2(m.net)}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#3D0A0A] text-white">
                <td colSpan={4} className="border border-stone-200 px-4 py-3 text-xs font-bold uppercase tracking-wider">
                  Total · {rows.length} booking{rows.length === 1 ? '' : 's'}
                </td>
                <td className="border border-stone-200 px-4 py-3 text-sm font-bold tabular-nums">{n2(totals.rate)}</td>
                <td className="border border-stone-200 px-4 py-3 text-sm font-bold tabular-nums">{n2(totals.gst)}</td>
                <td className="border border-stone-200 px-4 py-3 text-sm font-bold tabular-nums">{n2(totals.commission)}</td>
                <td className="border border-stone-200 px-4 py-3 text-sm font-bold text-[#D4A017] tabular-nums">{n2(totals.net)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-[10px] text-stone-400 mt-3">
          Rates are gross and GST-inclusive. GST is {pct(rates.gst_percent)}% of the gross amount; platform commission is{' '}
          {rates.is_active && Number(rates.percent) > 0 ? `${pct(rates.percent)}% of the GST-exclusive base` : 'currently not charged'}
          {rates.overridden ? ' (a rate agreed for your profile)' : ''}. A completed consultation keeps the figures it was settled at.
        </p>
      </div>
      )}
    </div>
  )
}

/** One label/value pair inside a mobile earnings card — the table's cell,
 *  restated with its column header attached (a card has no <thead> to lean on). */
function EarningFact({ label, value, dark = false }) {
  return (
    <div>
      <p className={`text-[8px] font-bold uppercase tracking-[0.18em] ${dark ? 'text-white/50' : 'text-stone-400'}`}>{label}</p>
      <p className={`text-xs font-semibold tabular-nums mt-1 ${dark ? 'text-white' : 'text-stone-800'}`}>{value ?? '—'}</p>
    </div>
  )
}
